import React, { useState, useCallback, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiMoreVertical,
  FiEdit,
  FiTrash2,
  FiCheckCircle,
  FiXCircle,
  FiEye,
} from "react-icons/fi";
import { toast } from "react-toastify";
import axios from "axios";

const api = axios.create({ baseURL: "https://algoritm-api-edu.uz" });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const MenuItem = memo(({ icon, text, hoverBg, onClick }) => (
  <motion.button
    whileHover={{ backgroundColor: hoverBg, x: 6 }}
    whileTap={{ scale: 0.97 }}
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      width: "100%",
      padding: "12px 18px",
      background: "white",
      border: "none",
      textAlign: "left",
      fontSize: "14.5px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
    }}
    onClick={onClick}
  >
    <span style={{ width: "18px" }}>{icon}</span>
    <span>{text}</span>
  </motion.button>
));

const TeacherActionsDropdown = memo(
  ({ teacher, onEdit, refreshList, onView }) => {
    const [open, setOpen] = useState(false);

    const handleConfirm = useCallback(async () => {
      if (!window.confirm("Tasdiqlansinmi?")) return;
      try {
        await api.put(`/api/admin/Teacher/Confirm/${teacher.id}`);
        toast.success("Tasdiqlandi");
        refreshList();
      } catch {
        toast.error("Xato");
      } finally {
        setOpen(false);
      }
    }, [teacher.id, refreshList]);

    const handleCancel = useCallback(async () => {
      if (!window.confirm("Bekor qilinsinmi?")) return;
      try {
        await api.put(`/api/admin/Teacher/Cancel/${teacher.id}`);
        toast.success("Bekor qilindi");
        refreshList();
      } catch {
        toast.error("Xato");
      } finally {
        setOpen(false);
      }
    }, [teacher.id, refreshList]);

    const handleDelete = useCallback(async () => {
      if (!window.confirm("O‘chirilsinmi?")) return;
      try {
        await api.delete(`/api/admin/Teacher/Delete/${teacher.id}`);
        toast.success("O‘chirildi");
        refreshList();
      } catch {
        toast.error("Xato");
      } finally {
        setOpen(false);
      }
    }, [teacher.id, refreshList]);

    const handleEdit = useCallback(() => {
      onEdit();
      setOpen(false);
    }, [onEdit]);

    const toggle = useCallback((e) => {
      e.stopPropagation();
      setOpen((v) => !v);
    }, []);

    const closeOnOutside = useCallback(
      (e) => {
        if (open) {
          e.stopPropagation();
          setOpen(false);
        }
      },
      [open]
    );

    return (
      <div style={{ position: "relative" }}>
        <motion.button
          whileHover={{ scale: 1.12 }}
          whileTap={{ scale: 0.92 }}
          onClick={toggle}
          style={{
            background: "rgba(255,255,255,0.8)",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "6px 8px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 100,
          }}
          aria-label="Amallar"
        >
          <FiMoreVertical size={20} color="#64748b" />
        </motion.button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: -8 }}
              transition={{
                duration: 0.18,
                type: "spring",
                stiffness: 500,
                damping: 30,
              }}
              style={{
                position: "absolute",
                top: "-10%",
                right: "100%",
                marginTop: "8px",
                background: "white",
                borderRadius: "12px",
                boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                overflow: "hidden",
                zIndex: 9999,
                minWidth: "180px",
                border: "1px solid #e2e8f0",
                pointerEvents: "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem
                icon={<FiEye color="#4f46e5" />}
                text="Ko‘rish"
                hoverBg="#eef2ff"
                onClick={() => {
                  if (onView) onView();
                  setOpen(false);
                }}
              />
              {teacher.canConfirm && (
                <MenuItem
                  icon={<FiCheckCircle color="#16a34a" />}
                  text="Tasdiqlash"
                  hoverBg="#f0fdf4"
                  onClick={handleConfirm}
                />
              )}
              {teacher.canCancel && (
                <MenuItem
                  icon={<FiXCircle color="#d97706" />}
                  text="Bekor qilish"
                  hoverBg="#fffbeb"
                  onClick={handleCancel}
                />
              )}
              {teacher.canEdit && (
                <MenuItem
                  icon={<FiEdit color="#2563eb" />}
                  text="Tahrirlash"
                  hoverBg="#eff6ff"
                  onClick={handleEdit}
                />
              )}
              {teacher.canDelete && (
                <MenuItem
                  icon={<FiTrash2 color="#dc2626" />}
                  text="O‘chirish"
                  hoverBg="#fee2e2"
                  onClick={handleDelete}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {open && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9998,
              cursor: "default",
            }}
            onClick={closeOnOutside}
          />
        )}
      </div>
    );
  }
);

TeacherActionsDropdown.displayName = "TeacherActionsDropdown";

export default TeacherActionsDropdown;
